import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaReply, 
  FaPaperPlane, 
  FaExchangeAlt, 
  FaBuilding,
  FaClock,
  FaUser,
  FaInfoCircle,
  FaRegPaperPlane,
  FaArrowRight,
  FaRegClock,
  FaRegCalendarAlt,
  FaChevronDown,
  FaSpinner,
  FaChevronUp,
  FaTag
} from 'react-icons/fa';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from '../CSS/ResponseTicketModal.module.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { db } from '../../firebase';
import { doc, onSnapshot, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';

// Configuración del editor de texto enriquecido
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'color', 'background'
];

// ============================================
// SERVICIO PARA ENVIAR NOTIFICACIONES POR EMAIL
// ============================================
const sendTicketNotification = async (tipo, ticket, destinatario, nombreDestinatario, nombreRemitente, textoRespuesta = '') => {
  try {
    // ⭐ Usar truebavirtual.onrender.com (tu servidor principal)
    const emailServerURL = 'https://truebavirtual.onrender.com';
    
    console.log('📧 Enviando notificación a:', `${emailServerURL}/api/email/tickets/notify`);
    
    const response = await fetch(`${emailServerURL}/api/email/tickets/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        tipo,
        ticket,
        destinatario,
        nombreDestinatario,
        nombreRemitente,
        textoRespuesta
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Notificación enviada:', result);
      return { success: true, messageId: result.messageId };
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      console.error('❌ Error en respuesta:', errorData);
      return { success: false, error: errorData.message || 'Error al enviar notificación' };
    }
  } catch (error) {
    console.error('❌ Error de red al enviar notificación:', error);
    return { success: false, error: error.message };
  }
};

// Extraer texto plano del HTML del editor
const extractPlainText = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const ResponseTicketModal = ({ 
  ticket: initialTicket, 
  user, 
  userRole,
  departamentos, 
  adminsDepartamento = [],
  onClose, 
  etiquetas = [],
  onResponder, 
  onEscalar 
}) => {
  // Estados
  const [ticket, setTicket] = useState(initialTicket);
  const [modo, setModo] = useState('responder');
  const [respuesta, setRespuesta] = useState('');
  const [departamentoDestino, setDepartamentoDestino] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActionPanel, setShowActionPanel] = useState(true);
  
  const messagesEndRef = useRef(null);
  const quillRef = useRef(null);
  const actionPanelRef = useRef(null);

  const canEscalate = (userRole === 'admin' || userRole === 'admin_departamento');
  
  // Obtener datos del creador del ticket
  const getTicketCreatorData = async (createdBy) => {
    try {
      // Si el ticket ya tiene email del creador
      if (ticket.created_by_email) {
        return {
          email: ticket.created_by_email,
          nombre: ticket.created_by_name || 'Usuario'
        };
      }
      
      // Buscar por ID en Firebase
      try {
        const userDoc = await getDoc(doc(db, 'users', createdBy));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          return {
            email: userData.email || userDoc.id,
            nombre: userData.displayName || 
                   `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 
                   'Usuario'
          };
        }
      } catch (error) {
        console.log('Error buscando por ID:', error);
      }
      
      // Fallback
      return {
        email: 'ticketrueba@colegiotrueba.net',
        nombre: ticket.created_by_name || 'Usuario'
      };
      
    } catch (error) {
      console.error('Error obteniendo datos del creador:', error);
      return {
        email: 'ticketrueba@colegiotrueba.net',
        nombre: 'Usuario'
      };
    }
  };
  
  // Obtener admin asignado
  const getAssignedAdminData = async (departamentoId) => {
    try {
      // Si el ticket ya tiene email del admin asignado
      if (ticket.assigned_to_email) {
        return {
          email: ticket.assigned_to_email,
          nombre: ticket.assigned_to_name || 'Admin'
        };
      }
      
      // Buscar en adminsDepartamento
      const adminsDelDepartamento = adminsDepartamento.filter(
        admin => admin.departamento_id === departamentoId
      );
      
      if (adminsDelDepartamento.length > 0) {
        const admin = adminsDelDepartamento[0];
        return {
          email: admin.email || 'ticketrueba@colegiotrueba.net',
          nombre: admin.nombre || admin.displayName || 'Admin'
        };
      }
      
      // Fallback
      return {
        email: 'ticketrueba@colegiotrueba.net',
        nombre: 'Administrador'
      };
      
    } catch (error) {
      console.error('Error al buscar admin:', error);
      return {
        email: 'ticketrueba@colegiotrueba.net',
        nombre: 'Administrador'
      };
    }
  };

  // Suscripción en tiempo real al ticket
  useEffect(() => {
    const ticketRef = doc(db, 'tickets', ticket.id);
    
    const unsubscribe = onSnapshot(ticketRef, (doc) => {
      if (doc.exists()) {
        const updatedTicket = {
          id: doc.id,
          ...doc.data(),
          created_at: doc.data().created_at?.toDate() || new Date(),
          updated_at: doc.data().updated_at?.toDate() || new Date(),
          closed_at: doc.data().closed_at?.toDate() || null
        };
        setTicket(updatedTicket);
      }
    }, (error) => {
      console.error("Error al escuchar cambios del ticket:", error);
    });
    
    return () => unsubscribe();
  }, [ticket.id]);
  
  // Auto-scroll a la última respuesta
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket.respuestas]);

  // Scroll automático al panel completo cuando se enfoca el editor (para móvil con teclado)
  useEffect(() => {
    const handleEditorFocus = () => {
      // Esperar a que el teclado virtual se muestre
      setTimeout(() => {
        if (actionPanelRef.current) {
          // Hacer scroll al panel completo para que todo sea visible
          actionPanelRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
          });

          // Pequeño ajuste adicional para asegurar que el botón sea visible
          setTimeout(() => {
            window.scrollBy({
              top: 20,
              behavior: 'smooth'
            });
          }, 100);
        }
      }, 350);
    };

    // Obtener el elemento del editor de Quill
    const quillEditor = quillRef.current?.getEditor();
    if (quillEditor) {
      const editorElement = quillEditor.root;
      editorElement.addEventListener('focus', handleEditorFocus);

      return () => {
        editorElement.removeEventListener('focus', handleEditorFocus);
      };
    }
  }, [quillRef.current]);
  
  // Datos computados
  const departamentoActual = departamentos.find(d => d.id === ticket.departamento_id);
  const departamentosDisponibles = departamentos.filter(
    d => d.id !== departamentoActual?.id
  );

  // Obtener etiquetas del ticket
  const getTicketEtiquetas = () => {
    if (!ticket.etiquetas || !Array.isArray(ticket.etiquetas) || ticket.etiquetas.length === 0) {
      return [];
    }
    
    return ticket.etiquetas
      .map(etiquetaId => etiquetas.find(e => e.id === etiquetaId))
      .filter(Boolean);
  };

  const ticketEtiquetas = getTicketEtiquetas();
  
  // Handlers
  const handleDepartamentoChange = (e) => {
    setDepartamentoDestino(e.target.value);
    if (errors.departamentoDestino) {
      setErrors(prev => ({ ...prev, departamentoDestino: '' }));
    }
  };

  const toggleActionPanel = () => {
    setShowActionPanel(!showActionPanel);
  };
  
  // Validación
  const validate = () => {
    const newErrors = {};
    
    if (modo === 'responder') {
      const textContent = extractPlainText(respuesta);
      if (!textContent.trim()) {
        newErrors.respuesta = 'La respuesta es obligatoria';
      }
    }
    
    if (modo === 'escalar' && !departamentoDestino) {
      newErrors.departamentoDestino = 'Debes seleccionar un departamento de destino';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Enviar respuesta con notificación por email
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      if (modo === 'responder') {
        // Guardar la respuesta
        await onResponder(ticket, respuesta);
        
        // Enviar notificación por email
        try {
          const esAdmin = userRole === 'admin' || userRole === 'admin_departamento';
          const textoRespuestaPlano = extractPlainText(respuesta);
          
          if (esAdmin) {
            // Admin responde → notificar al solicitante
            const creatorData = await getTicketCreatorData(ticket.created_by);
            
            if (creatorData && creatorData.email) {
              console.log('📧 Enviando email al usuario:', creatorData.email);
              const emailResult = await sendTicketNotification(
                'nueva_respuesta_admin',
                {
                  ...ticket,
                  departamento_nombre: departamentoActual?.nombre
                },
                creatorData.email,
                creatorData.nombre,
                user.displayName || user.nombre || 'Admin',
                textoRespuestaPlano
              );
              
              if (emailResult.success) {
                console.log('✅ Email enviado exitosamente');
              } else {
                console.warn('⚠️ No se pudo enviar el email:', emailResult.error);
              }
            }
          } else {
            // Usuario responde → notificar al admin
            const adminData = await getAssignedAdminData(ticket.departamento_id);
            
            if (adminData && adminData.email) {
              console.log('📧 Enviando email al admin:', adminData.email);
              const emailResult = await sendTicketNotification(
                'nueva_respuesta_usuario',
                {
                  ...ticket,
                  created_by_email: user.email,
                  created_by_name: user.displayName || user.nombre
                },
                adminData.email,
                adminData.nombre,
                user.displayName || user.nombre || 'Usuario',
                textoRespuestaPlano
              );
              
              if (emailResult.success) {
                console.log('✅ Email enviado exitosamente');
              } else {
                console.warn('⚠️ No se pudo enviar el email:', emailResult.error);
              }
            }
          }
        } catch (emailError) {
          console.error('❌ Error al enviar email (no crítico):', emailError);
          // No bloqueamos el flujo si falla el email
        }
        
        // Limpiar formulario
        setRespuesta('');
        setErrors({});
        
      } else if (modo === 'escalar' && canEscalate) {
        await onEscalar(ticket, departamentoDestino);
        onClose();
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ 
        submit: modo === 'responder' 
          ? 'Error al enviar la respuesta. Inténtalo de nuevo.' 
          : 'Error al escalar el ticket. Inténtalo de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return formatDistanceToNow(new Date(date), { 
        addSuffix: true, 
        locale: es 
      });
    } catch (error) {
      return '';
    }
  };
  
  // Formatear fecha completa
  const formatFullDate = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), "d 'de' MMMM 'a las' HH:mm", { locale: es });
    } catch (error) {
      return '';
    }
  };
  
  // Estado a texto
  const getEstadoText = (estado) => {
    const estados = {
      'nuevo': 'Nuevo',
      'abierto': 'Abierto',
      'en_proceso': 'En proceso',
      'escalado': 'Escalado',
      'cerrado': 'Cerrado'
    };
    return estados[estado] || estado;
  };
  
  // Obtener todos los mensajes ordenados cronológicamente
  const allMessages = [
    {
      id: 'original',
      texto: ticket.descripcion,
      fecha: ticket.created_at,
      usuario: ticket.created_by,
      usuario_nombre: ticket.created_by_name || 'Usuario',
      esOriginal: true
    },
    ...(ticket.respuestas || []).map((resp, index) => ({
      ...resp,
      id: `resp-${index}`,
      fecha: resp.fecha?.toDate ? resp.fecha.toDate() : new Date(resp.fecha)
    }))
  ].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  
  const visibleMessages = allMessages;
  
  // Animaciones
  const modalVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1, 
      transition: { duration: 0.3, type: "spring", damping: 25 } 
    },
    exit: { opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.2 } }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };
  
  return (
    <motion.div 
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={overlayVariants}
      onClick={onClose}
    >
      <motion.div 
        className={styles.modalContainer}
        variants={modalVariants}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2 className={styles.ticketTitle}>
              <span className={styles.ticketId}>{ticket.ticket_id || `#${ticket.id.slice(0, 6)}`}</span>
              <span className={styles.titleText}>{ticket.titulo}</span>
            </h2>
            
            <div className={styles.ticketMeta}>
              <div className={`${styles.ticketStatus} ${styles[`status${ticket.estado}`]}`}>
                {getEstadoText(ticket.estado)}
              </div>
              
              {ticketEtiquetas.length > 0 && (
                <div className={styles.ticketEtiquetas}>
                  {ticketEtiquetas.map(etiqueta => (
                    <div 
                      key={etiqueta.id}
                      className={styles.ticketEtiqueta}
                      style={{ backgroundColor: etiqueta.color }}
                    >
                      <FaTag className={styles.etiquetaIcon} />
                      {etiqueta.nombre}
                    </div>
                  ))}
                </div>
              )}
              
              <div className={styles.departamento}>
                <FaBuilding className={styles.metaIcon} />
                {departamentoActual?.nombre || 'Sin departamento'}
              </div>
            </div>
          </div>
          
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        {/* Body */}
        <div className={styles.modalBody}>
          <div className={styles.chatContainer}>
            {/* Info del ticket */}
            <div className={styles.ticketInfo}>
              <div className={styles.creatorInfo}>
                <div className={styles.creatorAvatar}>
                  {ticket.created_by_name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className={styles.creatorDetails}>
                  <span className={styles.creatorName}>
                    {ticket.created_by_name || 'Usuario'}
                  </span>
                  <span className={styles.creationDate}>
                    <FaRegCalendarAlt className={styles.dateIcon} />
                    {formatFullDate(ticket.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Mensajes/conversación */}
            <div className={styles.messagesContainer}>
              <AnimatePresence>
                {visibleMessages.map((message, index) => (
                  <motion.div 
                    key={message.id}
                    className={`${styles.messageWrapper} ${message.usuario === user.uid ? styles.ownMessage : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className={styles.messageBubble}>
                      {message.esOriginal && (
                        <div className={styles.originalTag}>Consulta original</div>
                      )}
                      
                      <div 
                        className={styles.messageContent}
                        dangerouslySetInnerHTML={{ __html: message.texto }}
                      />
                      
                      <div className={styles.messageFooter}>
                        <span className={styles.messageTime}>
                          <FaRegClock className={styles.timeIcon} />
                          {formatDate(message.fecha)}
                        </span>
                        
                        {message.usuario !== user.uid && (
                          <span className={styles.messageSender}>
                            {message.usuario_nombre || 'Usuario'}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Botón para mostrar panel cuando está oculto */}
          {!showActionPanel && (
            <div className={styles.hiddenPanelIndicator}>
              <button 
                className={styles.showPanelButton}
                onClick={toggleActionPanel}
                type="button"
              >
                <FaChevronUp className={styles.showIcon} />
              </button>
            </div>
          )}
          
          {/* Panel de acciones */}
          <AnimatePresence>
            {showActionPanel && (
              <motion.div
                className={styles.actionPanel}
                ref={actionPanelRef}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className={styles.actionPanelContent}>
                  {/* Selector de modo para admins */}
                  {canEscalate && departamentosDisponibles.length > 0 && (
                    <div className={styles.modeSelector}>
                      <div className={styles.modeButtons}>
                        <button
                          type="button"
                          className={`${styles.modeButton} ${modo === 'responder' ? styles.modeActive : ''}`}
                          onClick={() => setModo('responder')}
                        >
                          <FaReply className={styles.modeIcon} />
                          <span>Responder</span>
                        </button>
                        
                        <button
                          type="button"
                          className={`${styles.modeButton} ${modo === 'escalar' ? styles.modeActive : ''}`}
                          onClick={() => setModo('escalar')}
                        >
                          <FaExchangeAlt className={styles.modeIcon} />
                          <span>Escalar</span>
                        </button>
                      </div>
                      
                      <button 
                        className={styles.hidePanelButton}
                        onClick={toggleActionPanel}
                        type="button"
                      >
                        <FaChevronDown className={styles.hideIcon} />
                      </button>
                    </div>
                  )}

                  {/* Para usuarios que solo pueden responder */}
                  {(!canEscalate || departamentosDisponibles.length === 0) && (
                    <div className={styles.singleModeHeader}>
                      <div className={styles.singleModeTitle}>
                        <FaReply className={styles.modeIcon} />
                        <span>Responder</span>
                      </div>
                      
                      <button 
                        className={styles.hidePanelButton}
                        onClick={toggleActionPanel}
                        type="button"
                      >
                        <FaChevronDown className={styles.hideIcon} />
                      </button>
                    </div>
                  )}
                  
                  {/* Mensaje de error */}
                  {errors.submit && (
                    <div className={styles.errorMessage}>
                      <FaInfoCircle className={styles.errorIcon} />
                      {errors.submit}
                    </div>
                  )}
                  
                  {/* Formulario para escalar */}
                  {modo === 'escalar' && canEscalate && (
                    <div className={styles.escalationForm}>
                      <div className={styles.formGroup}>
                        <label htmlFor="departamentoDestino" className={styles.formLabel}>
                          Departamento de destino<span className={styles.requiredStar}>*</span>
                        </label>
                        <select
                          id="departamentoDestino"
                          className={styles.departmentSelect}
                          value={departamentoDestino}
                          onChange={handleDepartamentoChange}
                          disabled={isSubmitting}
                        >
                          <option value="">Selecciona un departamento</option>
                          {departamentosDisponibles.map(dep => (
                            <option key={dep.id} value={dep.id}>{dep.nombre}</option>
                          ))}
                        </select>
                        {errors.departamentoDestino && (
                          <div className={styles.fieldError}>{errors.departamentoDestino}</div>
                        )}
                      </div>
                      
                      <div className={styles.escalationNote}>
                        <FaInfoCircle className={styles.noteIcon} />
                        <p>
                          El ticket será transferido al departamento seleccionado.
                        </p>
                      </div>
                      
                      <div className={styles.escalateControls}>
                        <button
                          type="button"
                          onClick={handleSubmit}
                          className={`${styles.sendButton} ${styles.escalateButton}`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <FaSpinner className={styles.spinner} />
                              <span>Escalando...</span>
                            </>
                          ) : (
                            <>
                              <span>Escalar</span>
                              <FaArrowRight className={styles.sendIcon} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Área de respuesta */}
                  {modo === 'responder' && (
                    <form onSubmit={handleSubmit} className={styles.responseForm}>
                      <div className={styles.editorContainer}>
                        <ReactQuill
                          ref={quillRef}
                          value={respuesta}
                          onChange={setRespuesta}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Escribe tu respuesta..."
                          className={styles.richTextEditor}
                        />
                        
                        {errors.respuesta && (
                          <div className={styles.fieldError}>{errors.respuesta}</div>
                        )}
                      </div>
                      
                      <div className={styles.messageControls}>
                        <button
                          type="submit"
                          className={styles.sendButton}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <FaSpinner className={styles.spinner} />
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <span>Enviar</span>
                              <FaRegPaperPlane className={styles.sendIcon} />
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ResponseTicketModal;